/**
 * Auth context — wraps the SPA, fetches /me at mount, and exposes
 *   { principal, user, memberships, login, logout, refresh, serverConfig }
 *
 * Routes that need an authenticated user import `useAuth()` and check `user`.
 * The router-level guard lives in Shell.tsx — unauthenticated requests for
 * non-public routes redirect to /login. There is no public signup; new users
 * arrive via invitation.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  ApiError,
  api,
  type AuthConfigResponse,
  type LoginResponse,
  type PrincipalResponse,
} from '@/api/client.js';

/**
 * Login result the caller (the Login page) branches on:
 *   - 'ok' → session active, navigate into the SPA
 *   - 'password-change-required' → render the set-new-password panel; caller
 *     will exchange the challenge token via completePasswordChange()
 *   - 'two-factor-required' → render the TOTP prompt; caller exchanges via
 *     completeTwoFactor()
 */
export type LoginResult =
  | { status: 'ok' }
  | { status: 'password-change-required'; challengeToken: string; user: { email: string; name: string } }
  | { status: 'two-factor-required'; challengeToken: string; user: { email: string; name: string } };

interface AuthContextValue {
  status: 'loading' | 'authenticated' | 'anonymous';
  user: PrincipalResponse['user'];
  principal: PrincipalResponse['principal'] | null;
  memberships: PrincipalResponse['memberships'];
  /** Public auth-config from /api/v1/auth/config (which OAuth providers are configured). */
  serverConfig: AuthConfigResponse | null;
  login: (input: { email: string; password: string }) => Promise<LoginResult>;
  completePasswordChange: (input: { challengeToken: string; newPassword: string }) => Promise<void>;
  completeTwoFactor: (input: { challengeToken: string; code: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, setState] = useState<{
    status: 'loading' | 'authenticated' | 'anonymous';
    response: PrincipalResponse | null;
  }>({ status: 'loading', response: null });
  const [serverConfig, setServerConfig] = useState<AuthConfigResponse | null>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await api.auth.me();
      setState({ status: 'authenticated', response });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ status: 'anonymous', response: null });
      } else {
        throw err;
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
    void api.auth.config().then(setServerConfig).catch(() => setServerConfig(null));
  }, [refresh]);

  const value: AuthContextValue = {
    status: state.status,
    user: state.response?.user ?? null,
    principal: state.response?.principal ?? null,
    memberships: state.response?.memberships ?? [],
    serverConfig,
    async login(input): Promise<LoginResult> {
      const response: LoginResponse = await api.auth.login(input);
      if (response.status === 'password-change-required') {
        // Critically: do NOT mark authenticated. The cookie is intentionally
        // absent server-side until /auth/password-change succeeds.
        return {
          status: 'password-change-required',
          challengeToken: response.challengeToken,
          user: { email: response.user.email, name: response.user.name },
        };
      }
      if (response.status === 'two-factor-required') {
        // Same posture as the password-change branch — no session cookie
        // exists until /auth/2fa/verify succeeds.
        return {
          status: 'two-factor-required',
          challengeToken: response.challengeToken,
          user: { email: response.user.email, name: response.user.name },
        };
      }
      // status === 'ok' — strip the status tag to recover the legacy
      // PrincipalResponse shape consumers of `useAuth()` already use.
      const { status: _, ...rest } = response;
      setState({ status: 'authenticated', response: rest });
      return { status: 'ok' };
    },
    async completePasswordChange(input) {
      const response = await api.auth.passwordChange(input);
      setState({ status: 'authenticated', response });
    },
    async completeTwoFactor(input) {
      const response = await api.auth.twoFactor.verify(input);
      setState({ status: 'authenticated', response });
    },
    async logout() {
      await api.auth.logout();
      setState({ status: 'anonymous', response: null });
    },
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
